import { useInView } from 'react-intersection-observer'
import React, { useEffect, useState } from 'react'
import Spinner from './Spinner'
import classes from 'classnames'
import { groupBy as groupByFn } from 'lodash'
import { isPresentAndTruthy } from 'lib/helpers'
import language from 'services/Language'

type TSectionId = string

export type DirectionType = 'vertical' | 'grid'

export interface IProps<T> {
  items: Maybe<T[]>
  renderItem:
    | CallbackWithParams<T, number, React.ReactNode>
    | CallbackWith3Params<T, number, number, React.ReactNode>
  append?: Maybe<React.ReactNode>
  loadMore: Callback
  loadMoreIndexOffset?: number
  loading: boolean
  loadingMore: boolean
  emptyText?: React.ReactChild | string
  className?: string
  reverse?: boolean
  direction?: DirectionType

  // SectionList impl
  groupBy?: CallbackWithParam<T, TSectionId>
  renderSectionHeader?: CallbackWithParam<TSectionId, React.ReactChild | null>
}

function defaultGroupBy() {
  return '_'
}

function InfiniteList<T>({
  items,
  renderItem,
  loadMore,
  loadMoreIndexOffset = 3,
  loading,
  loadingMore,
  emptyText,
  className,
  append,
  reverse = false,
  direction = 'vertical',
  groupBy = defaultGroupBy,
  renderSectionHeader
}: IProps<T>) {
  const [showLoader, setShowLoader] = useState(false)
  const { ref, inView } = useInView()

  useEffect(() => {
    if (inView && !loadingMore) {
      loadMore()
    }
  }, [inView, loadingMore, loadMore])

  useEffect(() => {
    setShowLoader(loading || loadingMore)
  }, [loading, loadingMore])

  if (!items || !items.length) {
    if (showLoader) {
      return (
        <div className="py-3">
          <Spinner />
        </div>
      )
    }

    const emptyEl =
      emptyText && typeof emptyText === 'string' ? (
        <div className="mx-2">{emptyText}</div>
      ) : (
        emptyText
      )

    return (
      <div
        className={classes(
          `flex flex-1 overflow-auto`,
          reverse ? 'flex-col-reverse' : 'flex-col',
          className
        )}
      >
        <div className="text-gray-700">
          {emptyEl || (
            <div className="mx-2">{language.common.noItemsFound}</div>
          )}
        </div>
      </div>
    )
  }

  const intersectionEl = (
    <div className="h-[1px]" ref={ref} key="intersection_el">{`\u2004`}</div>
  )

  const groupedItems = groupByFn(items, groupBy)
  const groupedEntries = Object.entries(groupedItems)
  let overAllIndex = -1
  const flattenedItems = groupedEntries
    .map(([groupKey, groupItems]) => [
      renderSectionHeader?.(groupKey),
      groupItems.map((item, index) => {
        overAllIndex++
        return renderItem(item, index, overAllIndex)
      })
    ])
    .flat(2)
    .filter(isPresentAndTruthy)

  const shuffleIntersectionObserver =
    direction !== 'grid' && loadMoreIndexOffset > 0
  if (shuffleIntersectionObserver) {
    flattenedItems.splice(
      flattenedItems.length - 1 - loadMoreIndexOffset,
      0,
      intersectionEl
    )
  }

  return (
    <div
      className={classes(
        className,
        direction === 'vertical' && `flex flex-1 overflow-auto`,
        direction === 'vertical' && (reverse ? 'flex-col-reverse' : 'flex-col'),
        direction === 'grid' &&
          'grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 justify-items-stretch items-stretch'
      )}
      style={{ flexBasis: 0, position: 'relative' }}
    >
      {flattenedItems}
      {append}

      {showLoader && (
        <div className="py-3" key="loader">
          <Spinner />
        </div>
      )}
      {!showLoader && !shuffleIntersectionObserver && intersectionEl}
    </div>
  )
}

export default InfiniteList
